import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LogtSearchCriteria } from '../models/logTSearchCriteria';
import { generateCSV, appInsights } from '../appInsights';
import { ROUTES } from '../constants/appConstants';
import { formatFlightNumber, calculateRelativeRange } from './Overview.utils';
import { OverviewProps } from './Overview.types';
import { useOverviewState } from './useOverviewState';
import { clearLogTsAction } from '../actions/logTActions';

export const useOverview = (props: OverviewProps) => {
  const dispatch = useDispatch();
  const state = useOverviewState(props);

  // Clear Redux records when Overview mounts (navigated to from another page)
  useEffect(() => {
    dispatch(clearLogTsAction() as any);
  }, []);
  const {
    airline, acNumber, flightNumber, payload, messageType, smi,
    startDate, endDate, currentPage, sortColumn, sortDirection, sortLoading,
    fromTo, isXml, localLimit, dateRangeMode, relativeTimeValue, relativeTimeUnit,
    orig, dest, lastSearchCriteria, csvStartDate, csvEndDate, reportType,
    totalPages, pageInput,
    setShowDetails, setSelectedMessage, setCurrentPage, setPageInput,
    setLocalLoading, setTableLoading, setIsSearchLoading,
    setSortColumn, setSortDirection, setSortLoading,
    setSearchExecuted, setSearchAllCriteria, setLastSearchCriteria, setSearchHistory,
    setStartDate, setEndDate, setIsCSVLoading, setDateRangeMode,
    searchLogTs, userGroups, userName, userEmail,
  } = state;

  // Handler for relative time range changes
  const handleRelativeTimeChange = useCallback(() => {
    if (relativeTimeValue <= 0) return;
    const now = new Date();
    const end = new Date(now);
    let start = new Date(now);
    switch (relativeTimeUnit) {
      case 'min':
        start.setMinutes(start.getMinutes() - relativeTimeValue);
        break;
      case 'hours':
        start.setHours(start.getHours() - relativeTimeValue);
        break;
      case 'days':
        start.setDate(start.getDate() - relativeTimeValue);
        break;
      default:
        start.setMinutes(start.getMinutes() - relativeTimeValue);
    }
    setStartDate(start);
    setEndDate(end);
  }, [relativeTimeValue, relativeTimeUnit]);

  // Effect to recalculate dates when relative time value or unit changes
  useEffect(() => {
    if (dateRangeMode === 'relative') { handleRelativeTimeChange(); }
  }, [dateRangeMode, relativeTimeValue, relativeTimeUnit, handleRelativeTimeChange]);

  // Handler for date range mode changes
  const handleDateRangeModeChange = useCallback((mode: 'relative' | 'specific') => {
    setDateRangeMode(mode);
    if (mode === 'relative') {
      handleRelativeTimeChange();
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  }, [handleRelativeTimeChange]);

  const fetchData = useCallback(async (criteria: LogtSearchCriteria, isNewSearch = false) => {
    setTableLoading(true);
    if (isNewSearch) { setIsSearchLoading(true); }
    const pg = criteria.Page ?? 1;
    const lim = criteria.Limit ?? 10;
    const accessToken = userGroups?.userData?.access_token || '';
    await searchLogTs(pg, lim, criteria, accessToken);
    setTableLoading(false);
    if (isNewSearch) { setIsSearchLoading(false); }
    setSearchExecuted(true);
    setSearchAllCriteria(criteria);
  }, [searchLogTs, userGroups?.userData?.access_token]);

  const fetchDataForPagination = useCallback(async (criteria: LogtSearchCriteria) => {
    setTableLoading(true);
    const pg = criteria.Page ?? 1;
    const lim = criteria.Limit ?? 10;
    const accessToken = userGroups?.userData?.access_token || '';
    await searchLogTs(pg, lim, criteria, accessToken);
    setTableLoading(false);
    setSearchAllCriteria(criteria);
  }, [searchLogTs, userGroups?.userData?.access_token]);

  const handlePageClick = (pageNum: number) => {
    if (pageNum !== currentPage) {
      setCurrentPage(pageNum);
      setPageInput(String(pageNum));
      setShowDetails(false);
      fetchDataForPagination({ ...lastSearchCriteria, Page: pageNum, Limit: localLimit });
    }
  };

  const handleNextPage = useCallback(() => {
    if (!lastSearchCriteria) return;
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    setPageInput(String(newPage));
    setShowDetails(false);
    fetchDataForPagination({ ...lastSearchCriteria, Page: newPage, Limit: localLimit });
  }, [currentPage, lastSearchCriteria, fetchDataForPagination, localLimit]);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1 && lastSearchCriteria) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      setPageInput(String(newPage));
      setShowDetails(false);
      fetchDataForPagination({ ...lastSearchCriteria, Page: newPage, Limit: localLimit });
    }
  }, [currentPage, lastSearchCriteria, fetchDataForPagination, localLimit]);

  const handleSearch = useCallback(() => {
    setShowDetails(false);
    setSelectedMessage(null);
    setCurrentPage(1);
    setPageInput("1");
    setLocalLoading(true);

    let searchStartDate = startDate;
    let searchEndDate = endDate;
    if (dateRangeMode === 'relative') {
      const { start, end } = calculateRelativeRange(relativeTimeValue, relativeTimeUnit);
      searchStartDate = start;
      searchEndDate = end;
      setStartDate(start);
      setEndDate(end);
    }

    const formattedFlightNumber = formatFlightNumber(flightNumber, airline);
    const criteria: LogtSearchCriteria = {
      Airline: airline,
      TailNumber: acNumber,
      FlightNumber: formattedFlightNumber,
      KeywordSearch: payload,
      StartDate: searchStartDate ?? undefined,
      EndDate: searchEndDate ?? undefined,
      MessageType: messageType,
      SMI: smi,
      Source: "",
      IsXml: isXml,
      SortColumn: sortColumn,
      SortDirection: sortDirection,
      Page: 1,
      Limit: localLimit,
      Orig: orig,
      Dest: dest
    };

    setLastSearchCriteria(criteria);
    fetchData(criteria, true).finally(() => { setLocalLoading(false); });

    appInsights.trackEvent({
      name: "SearchPerformed",
      properties: {
        timestamp: new Date().toISOString(),
        userName, userEmail, airline,
        tailNumber: acNumber,
        flightNumber: formattedFlightNumber,
        keywordSearch: payload,
        messageType, smi, fromTo, isXml,
        startDate, endDate, orig, dest
      },
    });

    setSearchHistory((prev: any[]) => [
      ...prev,
      {
        timestamp: new Date().toISOString(),
        userName, userEmail, airline,
        tailNumber: acNumber,
        flightNumber: formattedFlightNumber,
        keywordSearch: payload,
        messageType, smi, fromTo, isXml, startDate, endDate
      },
    ]);
  }, [airline, acNumber, flightNumber, payload, startDate, endDate, messageType, smi, fromTo, isXml, sortColumn, sortDirection, localLimit, dateRangeMode, relativeTimeValue, relativeTimeUnit, calculateRelativeRange]);

  const handleRowClick = useCallback((row: { original: any }) => {
    setSelectedMessage(row.original);
    setShowDetails(true);
  }, []);

  const handleSort = useCallback(async (column: string) => {
    if (sortLoading) return;
    let direction: 'ASC' | 'DESC';
    if (sortColumn === column) {
      direction = sortDirection === 'ASC' ? 'DESC' : 'ASC';
    } else {
      direction = 'DESC';
    }
    setSortColumn(column);
    setSortDirection(direction);
    setSortLoading(true);
    setTableLoading(true);

    let sortStartDate = startDate;
    let sortEndDate = endDate;
    if (dateRangeMode === 'relative') {
      const { start, end } = calculateRelativeRange(relativeTimeValue, relativeTimeUnit);
      sortStartDate = start;
      sortEndDate = end;
    }

    const criteria: LogtSearchCriteria = {
      Airline: airline, TailNumber: acNumber,
      FlightNumber: formatFlightNumber(flightNumber, airline),
      StartDate: sortStartDate ?? undefined,
      EndDate: sortEndDate ?? undefined,
      MessageType: messageType, SMI: smi, Source: fromTo,
      IsXml: isXml, SortColumn: column, SortDirection: direction,
      Page: currentPage, Limit: localLimit, Orig: orig, Dest: dest
    };

    const accessToken = userGroups?.userData?.access_token || '';
    await searchLogTs(currentPage, localLimit, criteria, accessToken);
    setSortLoading(false);
    setTableLoading(false);
  }, [
    sortColumn, sortDirection, airline, acNumber, flightNumber,
    startDate, endDate, messageType, smi, currentPage, localLimit,
    searchLogTs, fromTo, isXml, sortLoading,
    dateRangeMode, relativeTimeValue, relativeTimeUnit, calculateRelativeRange
  ]);

  const handleGoClick = () => {
    const pageNumber = Number(pageInput);
    if (
      pageInput !== "" &&
      pageNumber >= 1 &&
      pageNumber <= totalPages &&
      pageNumber !== currentPage
    ) {
      setCurrentPage(pageNumber);
      setShowDetails(false);
      fetchDataForPagination({ ...lastSearchCriteria, Page: pageNumber, Limit: localLimit });
    }
  };

  const handleAdminClick = useCallback(() => {
    globalThis.open(ROUTES.ADMIN, '_blank');
  }, []);

  const handleCsvDownload = async () => {
    if (!csvStartDate || !csvEndDate) {
      alert("Please select a valid date range in the modal before downloading CSV.");
      return;
    }
    setIsCSVLoading(true);
    try {
      await generateCSV(csvStartDate, csvEndDate, reportType);
    } catch (error) {
      console.error("Error generating CSV:", error);
      alert("Failed to download CSV. Check console for details.");
    } finally {
      setIsCSVLoading(false);
    }
  };

  return {
    ...state,
    // Handlers
    handleRelativeTimeChange,
    handleDateRangeModeChange,
    handleSearch, handleRowClick, handleSort,
    handlePageClick, handleNextPage, handlePreviousPage,
    handleGoClick, handleAdminClick, handleCsvDownload,
    fetchData, fetchDataForPagination,
  };
};
